import { hasCategoryAndEventName } from ".";
import type { CodeLocation } from "../utils/CodeLocation";
import { UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { PrimitiveOrObjectWithOnlyPrimitiveFields, Primitive } from "../utils/Primitives";
import { EventBase } from "./EventBase";


export type {CodeLocation}

export interface InstrumentationPointUuidField {
	instrumentationPointUuid: UUID;
}

export interface BaseInstrumentationEvent<
	NAME extends string,
	DATA extends object,
> extends EventBase<'Instrumentation', NAME, DATA> {
	windowLocationHref: string;
	frameId?: number;
	documentId?: string;
}

export interface BaseInstrumentationEventWithCodeLocation<
	NAME extends string,
	DATA extends object
> extends BaseInstrumentationEvent<NAME, DATA>, CodeLocation {}

export interface BaseInstrumentationEventWithCodeLocationAndInstrumentationPointUuid<
	NAME extends string,
	DATA extends object
> extends BaseInstrumentationEvent<NAME, DATA>, CodeLocation, InstrumentationPointUuidField {}

export type KnownWindowEventType = keyof WindowEventMap;
export type ListenerEventType = KnownWindowEventType | string;
export type ListenerEvent<ET extends ListenerEventType = ListenerEventType> =
	ET extends (keyof WindowEventMap) ?
		// Known DOM events should be restricted to known properties
		WindowEventMap[ET] :
		// Unknown  events should allow any property
		(Event & Record<string, Primitive>);

export type ListenerEventPropName<ET extends ListenerEventType = ListenerEventType> = keyof ListenerEvent<ET>;


export interface EventListenerFields<EVENT_TYPE extends ListenerEventType = ListenerEventType> {
	domOrCustomEventName: EVENT_TYPE;
	elementTag: string;
	valuesAsString?: string;
	mouseEventFieldsRead?: MouseEventFields[];
	/** This field identifies an event handler so that we can note when that handler later reads properties via
	 * the `InstrumentationEventDomEventPropsRead`
	 */
	eventListenerUuid: UUID;
	propsRead: ListenerEventPropName<EVENT_TYPE>[];
	propsReadInstrumentationFailed?: boolean;
}

export interface MouseEventFields {
	timeStamp: number;
	clientX?: number;
	clientY?: number;
	pageX?: number;
	pageY?: number;
	screenX?: number;
	screenY?: number;
	offsetX?: number;
	offsetY?: number;
	x?: number;
	y?: number;
	movementX?: number;
	movementY?: number;
	layerX?: number;
	layerY?: number;

}
export const KeyOfMouseEventFieldsToMonitor = [
	'clientX',
	'clientY',
	'pageX',
	'pageY',
	'screenX',
	'screenY',
	'offsetX',
	'offsetY',
	"x",
	"y",
	"movementX",
	"movementY",
	"layerX",
	"layerY",
] as const satisfies (keyof MouseEventFields)[];
export type KeyOfMouseEventFieldsToMonitor = typeof KeyOfMouseEventFieldsToMonitor[number];
export const SetOfKeyOfMouseEventFieldsToMonitor = new Set(KeyOfMouseEventFieldsToMonitor);
export const isKeyOfMouseEventFieldsToMonitor = (key: string): key is KeyOfMouseEventFieldsToMonitor => SetOfKeyOfMouseEventFieldsToMonitor.has(key as keyof Omit<MouseEventFields, 'timeStamp'>);

export const eventTypesWithPerEventPropertyReporting = ['keydown', 'keyup', 'keypress'] as const satisfies (keyof WindowEventMap)[];
export type EventTypeForWhichPropsAreReportedOnEveryEvent = typeof eventTypesWithPerEventPropertyReporting[number];
const eventTypeForWhichPropsAreReportedOnEveryEventSet = new Set(eventTypesWithPerEventPropertyReporting);
export const isEventTypeForWhichPropsAreReportedOnEveryEvent = (eventType: ListenerEventType): eventType is EventTypeForWhichPropsAreReportedOnEveryEvent =>
	eventTypeForWhichPropsAreReportedOnEveryEventSet.has(eventType as EventTypeForWhichPropsAreReportedOnEveryEvent);

export const setOfEventTypesWithPerEventPropertyReporting = new Set<EventTypeForWhichPropsAreReportedOnEveryEvent>(eventTypesWithPerEventPropertyReporting);
export const isEventForWhichToGenerateEventsWhenPropertiesAreRead = (eventType: ListenerEventType): eventType is EventTypeForWhichPropsAreReportedOnEveryEvent => setOfEventTypesWithPerEventPropertyReporting.has(eventType as EventTypeForWhichPropsAreReportedOnEveryEvent);

export type EventForWhichPropsAreReportedPerEvent<
	EVENT extends EventTypeForWhichPropsAreReportedOnEveryEvent= EventTypeForWhichPropsAreReportedOnEveryEvent
> = WindowEventMap[EVENT];

export type KeepOnlyPrimitiveFields<T> = 
  { [K in keyof T as (T[K] extends string | number | boolean | null | undefined ? K : never)]: T[K] }

export type ReplaceNonPrimitiveFieldsWithTrue<T> = 
  { [K in keyof T]?: T[K] extends string | number | boolean | null | undefined ? T[K] : true }


export type ObservedWindowEventFieldsRead<T extends EventTypeForWhichPropsAreReportedOnEveryEvent = EventTypeForWhichPropsAreReportedOnEveryEvent> = ReplaceNonPrimitiveFieldsWithTrue<WindowEventMap[T]>;

export const DomEventsToInstrument = [
	'input',
	'change',
	'focusin',
	'click',
	'submit',
	'keydown',
	'paste',
] as const satisfies (keyof WindowEventMap)[];
export type DomEventToInstrument = (typeof DomEventsToInstrument)[number];
const DomEventsToInstrumentSet = new Set(DomEventsToInstrument);
export const isDomEventToInstrument = (eventName: string): eventName is DomEventToInstrument => DomEventsToInstrumentSet.has(eventName as DomEventToInstrument);



/**
 * Records a call to addEventListener as detected by instrumentation in the main-world content script.
 */
export interface InstrumentationEventAddEventListenerCalled<
	LISTENER_EVENT_TYPE extends ListenerEventType = ListenerEventType
> extends BaseInstrumentationEventWithCodeLocation<
	'AddEventListenerCalled',
	EventListenerFields<LISTENER_EVENT_TYPE>
> {
}

/**
 * Records a DOM event heard by the isolated-world content script.
 * 
 * The isolated-world content script instruments the page by calling addEventListener
 * at the top of the DOM hierarchy and as much shadow dom as it can receive.
 */
export interface InstrumentationEventDomEventFiredRaw<
	DOM_EVENT_NAME extends DomEventToInstrument = DomEventToInstrument
>{
	domOrCustomEventName: DOM_EVENT_NAME;
	elementType: string;
	elementUUID: ReturnType<typeof crypto.randomUUID>;
	elementId?: string;
	elementName?: string;
	whenObservedMs: number;
	value?: string;
	domEvent: PrimitiveOrObjectWithOnlyPrimitiveFields<WindowEventMap[DOM_EVENT_NAME]>;
}

/**
 * Records a DOM event heard by the isolated-world content script via its calls to addEventListener
 * on the DOM and as much shadow dom as it can successfully instrument.
 * 
 * Only listens to events of type `DomEventToInstrument`
 */
export interface InstrumentationEventDomEventFired<
	DOM_EVENT_NAME extends DomEventToInstrument = DomEventToInstrument
> extends BaseInstrumentationEvent<'DomEventFired',
	InstrumentationEventDomEventFiredRaw<DOM_EVENT_NAME>
> {}
export const isDomEventFiredEvent = hasCategoryAndEventName('Instrumentation', 'DomEventFired');

export interface InstrumentationEventDomEventReceivedRaw <
	EVENT_TYPE extends ListenerEventType = ListenerEventType
> extends EventListenerFields<EVENT_TYPE> {
	/** This field matches the  */
	eventListenerUuid: UUID;
	receivedEventUuid: UUID;
	target?: {
		id?: Element['id'];
		tag?: Element['tagName'];
		name?: string;
		value?: string;
	}
	whenObservedMs: number;
	// whenRecordedMs: number;

	domEvent: PrimitiveOrObjectWithOnlyPrimitiveFields<ListenerEvent<EVENT_TYPE>>;
	propsRead: ListenerEventPropName<EVENT_TYPE>[];
	propsReadInstrumentationFailed?: boolean;
}
/**
 * Records an update to the set of properties that one of the instrumented event listeners has read.
 * 
 * Fired only for events of the type `EventTypeForWhichPropsAreReportedOnEveryEvent`.
 * 
 * For every event instance fired, there will be one of these events created for *every* listener that
 * receives the event, and subsequent events fired when a listener reads properties that have not yet
 * been reported as read
 * 
 * These events are created by the main-world content script.
 **/
export interface InstrumentationEventDomEventReceived<
	T extends string = string
> extends BaseInstrumentationEvent<`DomEventReceived`,
	InstrumentationEventDomEventReceivedRaw<T>> {
	codeUrls: string[];
};

/**
 * Records a call to postMessage (when the observed frame is communicating with another frame/tab/window
 */
export interface InstrumentationEventPostMessageCalled extends BaseInstrumentationEventWithCodeLocation<`PostMessageCalled`, {
	target: 'Window' | 'EventSource';
	message: unknown;
	targetOrigin?: string;
	transfer?: Transferable[];
}> {};

/**
 * Report when the a message event is received by an 'message' event handler..
 * 
 * Reported by the main-world content script
 */
export interface InstrumentationEventDomMessageEventReceived extends BaseInstrumentationEvent<`MessageEventReceived`, {
	eventListenerUuid: UUID;
	data: unknown;
	eventPhase: number;
	lastEventId: string;
	origin: string;
	timeStamp: number;
}> {
	codeUrls: string[];
};

export interface InstrumentationEventAPIAccessedCanvasRenderingContext extends BaseInstrumentationEventWithCodeLocation<'AccessedCanvasRenderingContext', {
	contextType: Parameters<HTMLCanvasElement['getContext']>[0];
}> {
}

// By comparison, there is a list of [properties recorded by OpenWPM](https://github.com/openwpm/OpenWPM/blob/master/openwpm/js_instrumentation_collections/fingerprinting.json)

export const MonitoredNavigatorProperties = [
	'language',
	'platform',
	'hardwareConcurrency',
	'deviceMemory',
] as const satisfies (keyof Navigator | 'deviceMemory')[];
export type MonitoredNavigatorProperty = (typeof MonitoredNavigatorProperties)[number];
export interface InstrumentationEventAPIAccessedNavigator<NP extends MonitoredNavigatorProperty=MonitoredNavigatorProperty> extends BaseInstrumentationEventWithCodeLocation<'AccessedNavigatorProperty', {
	property: NP;
	value:  NP extends 'deviceMemory' ? number :
		Navigator[NP & keyof Navigator];
}> {
}

export const GeolocationPositionTransferable = (geolocationPosition: GeolocationPosition) => {
	const {coords: c, timestamp} = geolocationPosition;
	// Note: GeolocationPosition and Coordinates are not enumerable, so we can't just copy
	// them via a rest expression ({...geolocationPosition} or {...coords}).
	// They also can't be sent as an event!
	const coords = {
		latitude: c.latitude,
		longitude: c.longitude,
		accuracy: c.accuracy,
		altitude: c.altitude,
		altitudeAccuracy: c.altitudeAccuracy,
		heading: c.heading,
		speed: c.speed,
	};
	return {coords,timestamp};
}
export type GeolocationPositionTransferable = ReturnType<typeof GeolocationPositionTransferable>;

export const GeolocationPositionErrorTransferable = (GeolocationError: GeolocationPositionError) => ({
	code: GeolocationError.code,
	message: GeolocationError.message,
})
export type GeolocationPositionErrorTransferable = ReturnType<typeof GeolocationPositionErrorTransferable>;

interface WithPositionOptions {
	options: PositionOptions | undefined;
}

export interface InstrumentationEventGeolocationRequest extends BaseInstrumentationEventWithCodeLocationAndInstrumentationPointUuid<
	`GeolocationRequest.getCurrentPosition` |
	`GeolocationRequest.watchPosition`, WithPositionOptions & {
}> {}
export interface InstrumentationEventGeolocationGetCurrentPositionRequestSuccess extends BaseInstrumentationEventWithCodeLocationAndInstrumentationPointUuid<
	`GeolocationRequest.getCurrentPosition.success`, WithPositionOptions & {
	position: GeolocationPositionTransferable;
}> {}
export interface InstrumentationEventGeolocationWatchPositionSuccess extends BaseInstrumentationEventWithCodeLocationAndInstrumentationPointUuid<
	`GeolocationRequest.watchPosition.success`, WithPositionOptions & {
	position: GeolocationPositionTransferable;
}> {}
export type InstrumentationEventGeolocationRequestSuccess = 
	InstrumentationEventGeolocationGetCurrentPositionRequestSuccess |
	InstrumentationEventGeolocationWatchPositionSuccess;

export interface InstrumentationEventGeolocationRequestError extends BaseInstrumentationEventWithCodeLocationAndInstrumentationPointUuid<
	`GeolocationRequest.getCurrentPosition.error` |
	`GeolocationRequest.watchPosition.error`, WithPositionOptions & {
	positionError: GeolocationPositionErrorTransferable;
}> {}

export interface InstrumentationEventRespondedToCookiePrompt extends BaseInstrumentationEvent<'RespondedToCookiePrompt', {
	regExpString?: string;
	selector?: string | string[];
	elementTag?: string;
	elementId?: string;
	elementText?: string;
	action?: 'click';
}> {}

export interface InstrumentationEventScriptElementAdded extends BaseInstrumentationEventWithCodeLocation<'ScriptElementAdded', {
	src?: string;
	srcdoc?: string;
	innerText?: string;
	installingScriptUrl?: string;
}> {}

export interface InstrumentationEventElementWithSrcAttributeAdded extends BaseInstrumentationEventWithCodeLocation<'ElementWithSrcAttributeAdded', {
	src: string;
	elementTag: string;
	installingScriptUrl?: string;
}> {}

export type MainWorldInstrumentationEvent =
	InstrumentationEventAddEventListenerCalled |
	InstrumentationEventDomEventReceived |
	InstrumentationEventPostMessageCalled |
	InstrumentationEventDomMessageEventReceived |
	InstrumentationEventGeolocationRequest |
	InstrumentationEventGeolocationRequestSuccess |
	InstrumentationEventGeolocationRequestError |
	InstrumentationEventAPIAccessedCanvasRenderingContext |
	InstrumentationEventAPIAccessedNavigator |
	InstrumentationEventRespondedToCookiePrompt |
	InstrumentationEventScriptElementAdded |
	InstrumentationEventElementWithSrcAttributeAdded |
	never;

/**
 * Events generated by instrumenting a page via content scripts (isolated and main world)
*/
export type InstrumentationEvent =
	MainWorldInstrumentationEvent |
	InstrumentationEventDomEventFired |
	never;


export const isRecordableInstrumentationEventObservedAddEventListener =
	hasCategoryAndEventName('Instrumentation', 'AddEventListenerCalled');

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
type testType = Extract<InstrumentationEventGeolocationRequestSuccess, {category: "Instrumentation", eventName: "GeolocationRequest.getCurrentPosition.success"}>;

